import {
  MAX_PRODUCT_EXCLUSIONS,
  UNCATEGORIZED,
  type UpdateExcludedProductsResponse,
} from '@avrahami/contracts';
import { countText, fill, isApiError } from '@avrahami/ui';
import { he } from '../i18n/he';
import { errorMessage } from './format';

/**
 * What a store shows of the catalog (adr/0023, adr/0047): by default what the original site shows
 * the store's customer, and otherwise the platform's choice. What the products table of a store
 * shows; the screens themselves only present.
 */

/** A catalog product ticked in the table: enough to act on it after the list moved on. */
export interface PickedProduct {
  id: string;
  name: string;
  sku: string;
}

/** Why a store does not show a product: the platform's choice, or the site (the default). */
export type HiddenReason = 'PLATFORM' | 'SITE';

/** A product the store does not show. A store that exists also knows why and where it is placed. */
export interface HiddenProduct extends PickedProduct {
  reason?: HiddenReason;
  /** The catalog's categories the product is placed in. */
  categoryIds?: readonly string[];
}

/** Products to show in the store again (`shown`), or to stop showing in it. */
export interface VisibilityChange {
  products: readonly PickedProduct[];
  shown: boolean;
}

/** The products not shown in a store that is still being set up, after a change. */
export function applyVisibility(
  hidden: readonly PickedProduct[],
  change: VisibilityChange,
): PickedProduct[] {
  return setPicked(hidden, change.products, !change.shown);
}

/** The products a search matches (name, SKU or model); all of them without a search. */
export function searchProducts<Product extends PickedProduct & { model?: string | null }>(
  products: readonly Product[],
  query: string,
): Product[] {
  const text = query.trim().toLowerCase();
  if (text === '') return [...products];
  return products.filter((product) =>
    [product.name, product.sku, product.model ?? ''].some((field) =>
      field.toLowerCase().includes(text),
    ),
  );
}

/**
 * The products of a list under each category that lists them (`UNCATEGORIZED`: in none of the
 * categories shown, `knownIds`). Built once, so every category row reads its products from it.
 */
export function productsByCategory<Product extends { categoryIds: readonly string[] }>(
  products: readonly Product[],
  knownIds: ReadonlySet<string>,
): Map<string, Product[]> {
  const byCategory = new Map<string, Product[]>();
  const add = (categoryId: string, product: Product) => {
    const list = byCategory.get(categoryId);
    if (list === undefined) byCategory.set(categoryId, [product]);
    else list.push(product);
  };
  for (const product of products) {
    const known = new Set(product.categoryIds.filter((id) => knownIds.has(id)));
    if (known.size === 0) add(UNCATEGORIZED, product);
    for (const id of known) add(id, product);
  }
  return byCategory;
}

/** The products under any of the given categories, each once. */
export function productsInCategories<Product extends PickedProduct>(
  byCategory: ReadonlyMap<string, readonly Product[]>,
  categoryIds: readonly string[],
): Product[] {
  const found = new Map<string, Product>();
  for (const categoryId of categoryIds) {
    for (const product of byCategory.get(categoryId) ?? []) found.set(product.id, product);
  }
  return [...found.values()];
}

/** Whether every product of a list is ticked, some of them, or none. */
export function pickState(
  products: readonly PickedProduct[],
  ticked: ReadonlySet<string>,
): 'all' | 'some' | 'none' {
  const count = products.filter((product) => ticked.has(product.id)).length;
  if (count === 0) return 'none';
  return count === products.length ? 'all' : 'some';
}

/** What the dialog asks before a change of one product, or of several. */
export function visibilityTitle(change: VisibilityChange): string {
  const words = he.exclusions;
  return countText(
    change.products.length,
    change.shown
      ? { one: words.showTitleOne, many: words.showTitleMany }
      : { one: words.hideTitleOne, many: words.hideTitleMany },
  );
}

/** Adds a product that is not chosen, or removes it when it is. */
export function togglePicked(
  chosen: readonly PickedProduct[],
  product: PickedProduct,
): PickedProduct[] {
  return chosen.some((item) => item.id === product.id)
    ? chosen.filter((item) => item.id !== product.id)
    : [...chosen, product];
}

/** Adds every listed product that is not chosen (`on`), or removes them all ("select all"). */
export function setPicked(
  chosen: readonly PickedProduct[],
  products: readonly PickedProduct[],
  on: boolean,
): PickedProduct[] {
  const changed = new Set(products.map((product) => product.id));
  const rest = chosen.filter((item) => !changed.has(item.id));
  return on ? [...rest, ...products] : rest;
}

/** How many more products the store may have excluded, given how many it already has. */
export function exclusionRoom(excluded: number): number {
  return Math.max(0, MAX_PRODUCT_EXCLUSIONS - excluded);
}

/** How many products are excluded, in words. */
export function exclusionCountText(count: number): string {
  return countText(count, { one: he.exclusions.countOne, many: he.exclusions.countMany });
}

/** How many products are ticked in the table, in words. */
export function pickedCountText(count: number): string {
  if (count === 0) return he.exclusions.table.noneChosen;
  return countText(count, {
    one: he.exclusions.table.chosenOne,
    many: he.exclusions.table.chosenMany,
  });
}

/** What a change of the exclusions did, from the counts the API answered with. */
export function exclusionChangeText(
  result: Pick<UpdateExcludedProductsResponse, 'added' | 'lifted' | 'addedToStore'>,
): string {
  const parts = [
    result.added === 0
      ? null
      : countText(result.added, { one: he.exclusions.addedOne, many: he.exclusions.addedMany }),
    result.lifted === 0
      ? null
      : countText(result.lifted, { one: he.exclusions.liftedOne, many: he.exclusions.liftedMany }),
    result.addedToStore === 0
      ? null
      : countText(result.addedToStore, {
          one: he.exclusions.copiedOne,
          many: he.exclusions.copiedMany,
        }),
  ].filter((part) => part !== null);
  return parts.length === 0 ? he.exclusions.unchanged : parts.join(' ');
}

/**
 * Why a change of the exclusions was refused: a product that left the catalog, too many
 * products, or a store that is not a customer's (409).
 */
export function exclusionErrorText(error: unknown): string {
  if (isApiError(error, 'BAD_REQUEST') && error.message === 'Too many excluded products') {
    return fill(he.exclusions.tooMany, { max: MAX_PRODUCT_EXCLUSIONS });
  }
  return errorMessage(error, {
    BAD_REQUEST: he.exclusions.invalid,
    VALIDATION_FAILED: he.exclusions.invalid,
    CONFLICT: he.exclusions.notCustomer,
  });
}
